import React, { useState, useEffect } from 'react';
import api from '../api/axiosConfig';
import { useToast } from '../hooks/useToast';
import { useNotification } from './ui/NotificationModal';

const Acenteler = () => {
  const { showSuccess, showError } = useToast();
  const showNotification = useNotification();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    contact_phone: '',
    email: ''
  });

  useEffect(() => {
    loadAgencies();
  }, []);

  const loadAgencies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/agencies');
      setAgencies(response.data.agencies || response.data);
    } catch (error) {
      console.error('Acenteler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Form validasyonu
      if (!formData.name.trim()) {
        showError('Acente adı zorunludur');
        return;
      }
      
      if (!formData.email.trim()) {
        showError('Email adresi zorunludur (giriş için gerekli)');
        return;
      }

      // Acente oluştur
      const agencyResponse = await api.post('/api/admin/agencies', {
        name: formData.name,
        contact_name: formData.contact_name || null,
        contact_phone: formData.contact_phone || null,
        email: formData.email || null
      });

      console.log('Acente oluşturuldu:', agencyResponse.data);

      // Formu temizle
      setFormData({
        name: '',
        contact_name: '',
        contact_phone: '',
        email: ''
      });

      // Listeyi yenile
      await loadAgencies();
      showSuccess('Acente başarıyla eklendi!');

    } catch (error) {
      console.error('Acente eklenemedi:', error);
      showError('Hata: ' + (error.response?.data?.detail || 'Acente eklenemedi'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      contact_name: '',
      contact_phone: '',
      email: ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteClick = async (agency) => {
    const confirmDelete = await showNotification(
      'Transfer Sistemi Mesajı',
      `"${agency.name}" acentesini silmek istediğinizden emin misiniz?\n\n⚠️ Bu işlem geri alınamaz!\n\n• Acenteye ait aktif transferler varsa silme işlemi başarısız olur\n• Acenteye ait kullanıcılar varsa önce onları silmeniz gerekir`,
      'warning'
    );

    if (!confirmDelete) return;

    try {
      setDeleteLoading(agency.id);
      
      const response = await api.delete(`/api/admin/agencies/${agency.id}`);
      
      showSuccess(response.data.message || 'Acente başarıyla silindi!');
      
      // Listeyi yenile
      await loadAgencies();
      
    } catch (error) {
      console.error('Acente silinemedi:', error);
      showError(error.response?.data?.detail || 'Acente silinirken hata oluştu');
    } finally {
      setDeleteLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Acente Ekleme Paneli */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <h2 className="text-xl font-bold mb-4 text-gray-900">Yeni Acente Ekle</h2>
          
          {/* Acente Bilgileri */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Acente Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1" htmlFor="agency-name">
                  Acente Adı *
                </label>
                <input
                  id="agency-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 focus:ring-red-500 focus:border-red-500 px-3 py-2"
                  placeholder="Acente adını giriniz"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1" htmlFor="contact-name">
                  İletişim Kişisi
                </label>
                <input
                  id="contact-name"
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => handleInputChange('contact_name', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 focus:ring-red-500 focus:border-red-500 px-3 py-2"
                  placeholder="İletişim kişisi adı"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1" htmlFor="contact-phone">
                  Telefon
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 focus:ring-red-500 focus:border-red-500 px-3 py-2"
                  placeholder="0212-555-0000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1" htmlFor="email">
                  Email *
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 focus:ring-red-500 focus:border-red-500 px-3 py-2"
                  placeholder="info@acente.com"
                  required
                />
              </div>
            </div>
          </div>

          {/* Butonlar */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium rounded-md bg-gray-200 text-gray-900 hover:bg-gray-300"
            >
              İptal
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium rounded-md bg-red-500 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Ekleniyor...' : 'Acente Ekle'}
            </button>
          </div>
        </div>

        {/* Acente Listesi */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-bold mb-4 text-gray-900">Kayıtlı Acenteler</h2>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto"></div>
              <p className="mt-2 text-gray-600">Yükleniyor...</p>
            </div>
          ) : agencies.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Henüz kayıtlı acente bulunmuyor
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Acente Adı
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      İletişim Kişisi
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Telefon
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Kayıt Tarihi
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Durum
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {agencies.map((agency) => (
                    <tr key={agency.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        #{agency.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {agency.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {agency.contact_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {agency.contact_phone || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {agency.email || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(agency.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Aktif
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {/* BHT Clinic (admin acentesi) için silme butonunu gösterme */}
                        {(agency.id === 1 || agency.email === 'admin@bhtclinic.com.tr' || agency.name === 'BHT Clinic') ? (
                          <span className="text-gray-400 text-xs">
                            Sistem Acentesi
                          </span>
                        ) : (
                          <button
                            onClick={() => handleDeleteClick(agency)}
                            disabled={deleteLoading === agency.id}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                            title="Acenteyi Sil"
                          >
                            {deleteLoading === agency.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                            <span className="hidden sm:inline">Sil</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>


      </div>
    </div>
  );
};

export default Acenteler;
